<template>
  <section name="theme-config" class="theme-config" v-aha-emit-action.view>
    <section class="preview-section">
    </section>
    <section class="config-section">
      <section class="p-4 border-b">
        <h2>{{ title }}</h2>
      </section>
      <section class="p-4 config-field">
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <span class="font-bold">{{ $i18nPresenter.t('Theme name') }}</span>
            <aha-input
              v-model="theme.themeName"
              :maxLength="50"
            />
          </div>
          <div class="divider bg-secondary-20 h-px"></div>
          <div class="flex flex-col gap-2">
            <span class="font-bold">{{ $i18nPresenter.t('Logo') }}</span>
            <img v-if="logoThumbnail" :src="logoThumbnail" class="w-full h-full object-cover">
            <aha-button
              class="flex-1"
              variant="primary"
              @click="isShowLogoUploadModal = true"
            >
              {{ $i18nPresenter.t('Add') }}
            </aha-button>
          </div>
          <div class="divider bg-secondary-20 h-px"></div>
          <div class="flex flex-col gap-2">
            <span class="font-bold">{{ $i18nPresenter.t('Background') }}</span>

            <img v-if="thumbnail" :src="thumbnail" class="w-full h-full object-cover">
            <div class="flex items-center justify-between gap-4">
              <aha-button
                class="flex-1"
                variant="primary"
                @click="isShowBackgroundUploadModal = true"
              >
                {{ $i18nPresenter.t('Add') }}
              </aha-button>
              <span class="inline-flex items-center justify-center">
                <input
                  type="color"
                  v-model="theme.baseColour"
                  :style="{'background-color': theme.baseColour}"
                >
              </span>
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <span
              class="font-bold"
              v-html="$i18nPresenter.t('backgroundVisibilityX', {
                visibility: theme.visibility
              })"
            ></span>
            <input
              type="range"
              min="0"
              max="100"
              v-model="theme.visibility"
            >
          </div>
          <div class="divider bg-secondary-20 h-px"></div>
          <div class="flex flex-col gap-2">
            <span class="font-bold">{{ $i18nPresenter.t('Text') }} </span>
            <div class="flex items-center justify-between">
              <SelectFont
                v-model="theme.fontFamily"
                :checkPlan="false"
              />
              <span class="inline-flex items-center justify-center">
                <input
                  type="color"
                  v-model="theme.textColour"
                  :style="{'background-color': theme.textColor}"
                >
              </span>
            </div>
          </div>
          <div class="divider bg-secondary-20 h-px"></div>
          <div class="flex flex-col gap-2">
            <span class="font-bold">{{ $i18nPresenter.t('Visualisation colours') }}</span>
            <ColorPaletteSelect
              :selectedColorPaletteId="theme.colorPalette?.id"
              :colorPalettes="presetColorPalettes"
              @selectColorPalette="(colorPalette) => theme.colorPalette = colorPalette"
            />
          </div>
        </div>
      </section>
      <section class="flex p-4">
        <aha-button
          :disabled="isCreating"
          :submitting="isCreating"
          class="flex-1"
          variant="primary"
          @click="saveTheme"
          size="xl"
        >
          {{ $i18nPresenter.t('Save') }}
        </aha-button>
      </section>
    </section>
    <UploadImageV2
      v-if="isShowLogoUploadModal"
      type="themeLogoConfig"
      @hideModal="isShowLogoUploadModal = false"
      :tabs="activeTabs"
      @callback="(imageUrl) => handleUploadLogo(imageUrl)"
      @selectExistImage="(obj) => handleUploadLogo(obj?.origin)"
    />
    <UploadImageV2
      v-if="isShowBackgroundUploadModal"
      type="themeBackgroundImageConfig"
      :tabs="activeTabs"
      @hideModal="isShowBackgroundUploadModal = false"
      @callback="(imageUrl) => handleUploadBackgroundImage(imageUrl)"
      @selectExistImage="(obj) => handleUploadBackgroundImage(obj?.origin)"
    />
    <CropImage
      :type="'logo'"
      v-if="isShowCropLogoModal && imageUrlCrop"
      :aspect-ratio="DEFAULT_IMAGE_RATIOS.LOGO"
      :image-url="imageUrlCrop"
      @hideModal="() => isShowCropLogoModal = false"
      @on-cancel="() => isShowCropLogoModal = false"
      @handleCropImageFinished="(data) => handleCropLogo(data)"
    />
    <CropImage
      v-if="isShowCropImageModal && imageUrlCrop"
      :aspect-ratio="DEFAULT_IMAGE_RATIOS.BACKGROUND_IMAGE"
      :image-url="imageUrlCrop"
      @on-cancel="() => isShowCropImageModal = false"
      @hideModal="() => isShowCropImageModal = false"
      @handleCropImageFinished="(data) => handlerCropBackgroundImage(data)"
    />
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CropImage from '@/components/presentations/CropImage.vue'
import UploadImageV2 from '@/components/presentations/UploadImage.v2.vue'
import SelectFont from '@/components-v2/font/select'
import ColorPaletteSelect from '@/components-v2/presentation-editor/side-right/right-nav/ColorPaletteSelect.vue'
import Constants from '@/constant'
import ActiveTabNames from '@/constant/ActiveTabNames'

export default {
  name: 'ThemeConfig',
  components: {
    UploadImageV2,
    SelectFont,
    ColorPaletteSelect,
    CropImage,
  },
  props: {
    title: {
      type: String,
      default: 'Create Theme',
    },
    themeName: {
      type: String,
      default: 'My new theme',
    },
    logo: {
      type: String,
      default: null,
    },
    baseColour: {
      type: String,
      default: '#ffffff',
    },
    backgroundImage: {
      type: String,
      default: null,
    },
    visibility: {
      type: Number,
      default: 100,
    },
    fontFamily: {
      type: String,
      default: 'Nunito',
    },
    textColour: {
      type: String,
      default: '#313131',
    },
    colorPalette: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      activeTabs: [ActiveTabNames.MY_IMAGES, ActiveTabNames.UPLOAD],
      isShowBackgroundUploadModal: false,
      isShowLogoUploadModal: false,
      isShowCropLogoModal: false,
      isShowCropImageModal: false,
      DEFAULT_IMAGE_RATIOS: Constants.DEFAULT_IMAGE_RATIOS,
      imageUrlCrop: null,
      theme: {
        themeName: this.themeName,
        logo: this.logo,
        baseColour: this.baseColour,
        backgroundImage: this.backgroundImage,
        visibility: this.visibility,
        fontFamily: this.fontFamily,
        textColour: this.textColour,
        colorPalette: this.colorPalette,
      },
      thumbnail: this.backgroundImage?.large || null,
      logoThumbnail: this.logo?.large || null,
    }
  },
  computed: {
    ...mapGetters('presetModule', ['presetColorPalettes']),
    ...mapGetters('themeModule', ['isCreating']),
  },
  methods: {
    ...mapActions('themeModule', ['setThemeAction']),
    async handleUploadLogo(imageUrl) {
      this.isShowLogoUploadModal = false
      this.isShowCropLogoModal = true
      this.imageUrlCrop = imageUrl
    },
    async handleCropLogo(data) {
      const { crop, key } = data
      this.isShowCropLogoModal = false
      this.theme.logo = key
      this.logoThumbnail = crop
    },
    async handleUploadBackgroundImage(imageUrl) {
      this.isShowBackgroundUploadModal = false
      this.isShowCropImageModal = true
      this.imageUrlCrop = imageUrl
    },
    async handlerCropBackgroundImage(data) {
      const { crop } = data
      this.isShowCropImageModal = false
      this.theme.backgroundImage = crop
      this.thumbnail = crop
    },
    saveTheme() {
      this.$emit('saveTheme', this.theme)
    },
  },
}
</script>

<style lang="scss" scoped>
.theme-config {
  display: grid;
  grid-template-columns: 1fr 384px;
  & .config-field {
    --height: 573px;
    @apply overflow-auto;

    height: var(--height);
    max-height: var(--height);
  }
}

input[type="color"] {
  @apply w-6 h-6 rounded-full border-secondary-20 border-2;
  appearance: none;
  border-style: solid;
  -webkit-appearance: none;
  overflow: hidden;
  padding: 0;
  cursor: pointer;
}

/* Remove default styling in WebKit browsers */
input[type="color"]::-webkit-color-swatch-wrapper {
  padding: 0;
}

input[type="color"]::-webkit-color-swatch {
  border: none;
  border-radius: 50%;
}

span:has(> input[type="color"]) {
  @apply border-secondary-20 p-2 rounded-lg;
  border-width: 1px;
  border-style: solid;
  cursor: pointer;
}
</style>
